import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type PayoutKYCRecord, type PayoutKYCStats, type PayoutKYCRail } from '@/lib/adminApi';
import {
  Card,
  DataTable,
  Spinner,
  EmptyState,
  Badge,
  StatTile,
  type Column,
  type BadgeTone,
} from '@/components/ui';
import { IconShield } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { PayoutKYCControl } from '@/components/admin/PayoutKYCControl';

const PAGE = 25;

// The payout RAIL → badge tone. A PayID/bank record is a usable payout target
// (ok); a record with neither is incomplete — nothing to pay out to yet (warn).
// Keep in sync with admin-svc's payoutKYCRail.
const RAIL_TONE: Record<PayoutKYCRail, BadgeTone> = {
  payid: 'ok',
  bank: 'info',
  none: 'warn',
};

function railLabel(rail: PayoutKYCRail): string {
  if (rail === 'payid') return 'PayID';
  if (rail === 'bank') return 'Bank (BSB)';
  return 'Incomplete';
}

function railBadge(k: PayoutKYCRecord) {
  return <Badge tone={RAIL_TONE[k.rail] ?? 'neutral'}>{railLabel(k.rail)}</Badge>;
}

// The verification status badge (migration 037). A record with no payout target
// can't be verified yet, so it reads as a neutral dash rather than "Pending".
function verifiedBadge(k: PayoutKYCRecord) {
  if (k.rail === 'none') return <span className="text-xs text-muted">—</span>;
  const verified = k.verified === true || k.verification_status === 'verified';
  return <Badge tone={verified ? 'ok' : 'warn'}>{verified ? 'Verified' : 'Pending'}</Badge>;
}

// The completeness facet chips, in display order. '' = all. Mirrors the legacy
// Filament status filter, reframed around what the record can actually pay out
// to (the driver_kyc table has no verified flag — see backend note).
const FACET_FILTERS: Array<{ value: '' | 'payid' | 'bank' | 'incomplete'; label: string }> = [
  { value: '', label: 'All' },
  { value: 'payid', label: 'PayID' },
  { value: 'bank', label: 'Bank (BSB)' },
  { value: 'incomplete', label: 'Incomplete' },
];

/** Driver Payout-KYC Verify — the driver bank / PayID records that gate Azupay
 *  earnings cash-outs, read from driver-svc's driver_kyc (driver-svc OWNS the
 *  record; admin-svc only READS through the shared pool). A "Review" button opens
 *  a drawer with the full bank/PayID detail + the gated payout-KYC verify control.
 *
 *  DEFENSIVE: shows a clean "not yet in Postgres" state when the driver_kyc table
 *  isn't in this DB yet. Covers the Filament DriverBankKycDetailResource list/view.
 *
 *  NOTE: the verify WRITE surfaces as "not available yet" until the owning service
 *  grows an internal verify endpoint (driver_kyc has no verification-status column
 *  today) — the control stays discoverable + audited rather than a dead button. */
export function PayoutKYC() {
  const [q, setQ] = useState('');
  const [facet, setFacet] = useState<'' | 'payid' | 'bank' | 'incomplete'>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<PayoutKYCRecord | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'payout-kyc', q, facet, offset],
    queryFn: ({ signal }) =>
      adminApi.payoutKYC({ q, facet: facet || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<PayoutKYCRecord>> = [
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.driver_name}</div>
          <div className="metric text-[11px] text-muted">#{r.driver_id}</div>
        </div>
      ),
    },
    {
      key: 'rail',
      header: 'Payout rail',
      cell: (r) => railBadge(r),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => verifiedBadge(r),
    },
    {
      key: 'bank',
      header: 'Bank',
      cell: (r) => <span className="text-xs text-slate-300">{r.bank_name || '—'}</span>,
    },
    {
      key: 'target',
      header: 'Pay target',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.pay_id ? r.pay_id : r.bsb ? `BSB ${r.bsb}` : '—'}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      cell: (r) => <span className="text-xs text-muted">{dateTime(r.updated_at)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Review
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Driver Payout-KYC Verify"
        subtitle="Driver bank / PayID records that gate Azupay earnings cash-outs. Open one to review the detail and verify."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by driver / bank / PayID…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Driver payout-KYC not yet in Postgres"
            description={
              data?.note ??
              'The driver bank/KYC records are owned by driver-svc and are not in this Postgres yet. This page lights up automatically once the table lands.'
            }
            icon={<IconShield className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {data?.stats ? <PayoutKYCStatsRow stats={data.stats} /> : null}

          <div className="mb-3 flex flex-wrap gap-1.5">
            {FACET_FILTERS.map((f) => {
              const active = f.value === facet;
              return (
                <button
                  key={f.value || 'all'}
                  type="button"
                  onClick={() => {
                    setFacet(f.value);
                    setOffset(0);
                  }}
                  className={
                    active
                      ? 'rounded-md bg-brand-500/20 px-2.5 py-1 text-xs font-medium text-brand-300 ring-1 ring-inset ring-brand-500/40'
                      : 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-inset ring-ink-700 hover:bg-ink-800'
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading payout-KYC…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load payout-KYC" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<PayoutKYCRecord>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.driver_id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No payout-KYC records"
                      description="No driver bank/PayID records match this filter."
                      icon={<IconShield className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <PayoutKYCDrawer record={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** The headline totals above the table: submitted, by rail, and incomplete. */
function PayoutKYCStatsRow({ stats }: { stats: PayoutKYCStats }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="Submitted" value={String(stats.total)} hint="drivers with details" />
      <StatTile label="PayID" value={String(stats.payid_count)} hint="Azupay PayID set" />
      <StatTile label="Bank (BSB)" value={String(stats.bank_count)} hint="bank transfer set" />
      <StatTile label="Incomplete" value={String(stats.incomplete_count)} hint="no payout target" />
    </div>
  );
}

/** PayoutKYCDrawer — the full bank/PayID detail + the gated payout-KYC verify
 *  control. The list row already carries every field, so no extra fetch is
 *  needed for the body. */
function PayoutKYCDrawer({ record: k, onClose }: { record: PayoutKYCRecord; onClose: () => void }) {
  const payIdTypeLabel = k.pay_id_type ? k.pay_id_type.toUpperCase() : '';
  return (
    <Drawer
      title={`Payout-KYC · ${k.driver_name}`}
      subtitle={`Driver #${k.driver_id} · ${railLabel(k.rail)}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <Field label="Payout rail" value={railBadge(k)} />
          <Field label="Driver" value={`${k.driver_name} (#${k.driver_id})`} />
          {k.phone ? <Field label="Phone" value={<span className="metric">{k.phone}</span>} /> : null}
          {k.email ? <Field label="Email" value={k.email} /> : null}
        </div>

        {/* Bank-transfer fields (BSB rail). */}
        <Card>
          <div className="mb-2 text-sm font-medium text-slate-100">Bank details</div>
          <Field label="Bank name" value={k.bank_name || '—'} />
          <Field label="Account name" value={k.account_name || '—'} />
          <Field
            label="Account number"
            value={<span className="metric">{k.bank_account_number || '—'}</span>}
          />
          <Field label="BSB" value={<span className="metric">{k.bsb || '—'}</span>} />
        </Card>

        {/* PayID fields (Azupay's preferred rail). */}
        <Card>
          <div className="mb-2 text-sm font-medium text-slate-100">PayID</div>
          <Field label="PayID" value={<span className="metric">{k.pay_id || '—'}</span>} />
          <Field label="PayID type" value={payIdTypeLabel || '—'} />
          <Field label="Last updated" value={dateTime(k.updated_at)} />
        </Card>

        {/* Payout-KYC verify for this driver (gated by payouts.kyc_verify). A
            REAL audited write — admin-svc owns the verification columns
            (migration 037). Degrades to a clean "not available yet" note until
            the table + migration land. */}
        <PayoutKYCControl driverId={k.driver_id} record={k} />
      </div>
    </Drawer>
  );
}
